/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=3%3A10
import type { PropsWithChildren } from 'react';
import React from 'react';
import type { Interpolation, Theme } from '@emotion/react';
import type { Theme as MuiTheme } from '@mui/material';
import { createTheme } from '@mui/material';
import type { GlobalToken } from 'antd';
import type { ITheme } from '../../styles/theme';
import { theme as defaultTheme } from '../../styles/theme';
import type { SettingBasedOnBreakpoints } from '../../../hooks/use-setting-based-on-breakpoints';
import { useSettingBasedOnBreakpoints } from '../../../hooks/use-setting-based-on-breakpoints';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import { ELEMENT_ID_DATA_TEST_ATTRIBUTE } from '../../../constants/data-element-tracking-ids';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';
import {
  TypographyVariant,
  useGetTypographyStyles,
} from './hooks/use-get-typography-styles';

interface IProps extends Partial<IPropsWithTestId> {
  variant?: TypographyVariant | SettingBasedOnBreakpoints<TypographyVariant>;
  theme?: ITheme;
  muiTheme?: MuiTheme;
  themeToken?: GlobalToken;
  color?: GlobalToken['colorWhite'];
  additionalStyles?: Interpolation<Theme>;
}

const Typography: React.FC<PropsWithChildren<IProps>> = ({
  children,
  variant = TypographyVariant.MEDIUM_BODY_REGULAR,
  theme = defaultTheme,
  muiTheme = createTheme(),
  themeToken,
  color,
  additionalStyles = {},
  dataElementId,
}) => {
  const { getSettingBasedOnBreakpoint } =
    useSettingBasedOnBreakpoints(muiTheme);
  const defaultThemeToken = useThemeToken();

  const currentVariant =
    typeof variant === 'string'
      ? variant
      : getSettingBasedOnBreakpoint(variant);
  const styles = useGetTypographyStyles(
    currentVariant,
    theme,
    themeToken ?? defaultThemeToken,
    color,
  );

  return (
    <div
      css={[styles, additionalStyles]}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId }}
    >
      {children}
    </div>
  );
};

export default Typography;
